import { CommandBar, ICommandBarItemProps, makeStyles } from '@fluentui/react';
import {
    FluentProvider,
    TeachingPopover,
    TeachingPopoverBody,
    TeachingPopoverHeader,
    TeachingPopoverSurface,
    TeachingPopoverTitle,
    useRestoreFocusTarget,
} from '@fluentui/react-components';
import { useId } from '@fluentui/react-hooks';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { DismissableContentName } from '../../constants/app';
import { useActionCreator } from '../../hooks/action-creator';
import {
    useDevMenuPanelContext,
    useHelpMenuPanelContext,
    useUserSettingsPanelContext,
} from '../../hooks/context/panels';
import { useCurrentFluent2Theme } from '../../hooks/styling';
import { Status } from '../../models/common';
import { dismissContent as dismissContentActionCreator } from '../../redux/actions/application/application-action-creators';
import { signIn } from '../../redux/actions/identity/identity-action-creators';
import {
    ActionAbility,
    DevBoxCreateAbilityState,
    EnvironmentCreateAbilityState,
} from '../../redux/selector/display/create-ability/models';
import {
    getDevBoxCreateAbilityState,
    getEnvironmentCreateAbilityState,
} from '../../redux/selector/display/create-ability/selectors';
import { getStatusForGetOrganization, getStatusForGetSignedInUser } from '../../redux/selector/graph-selectors';
import { getIsSignedIn } from '../../redux/selector/identity-selectors';
import { getIsHomeInitialized } from '../../redux/selector/sub-applications/home-selectors';
import { getHasMultipleTenants, getStatusForListTenants } from '../../redux/selector/tenant-selector';
import { getRemoteAppOptionsSelector } from '../../redux/selector/user-settings-selector';
import { AppSemanticColor } from '../../themes/app-semantic-colors';
import { ReturnVoid } from '../../types/return-void';
import { getSemanticColor } from '../../utilities/styles';
import { getIsConnectViaAppTeachBubbleDismissed } from '../dev-box/selectors';
import { getFarItems, getIsSettingsMenuTeachBubbleDismissed } from './selectors';
import SignInButton from './sign-in-button';
import Title from './title';

interface TitleBarComponentProps {
    hasMultipleTenants: boolean;
    isDevMenuPanelOpen: boolean;
    isUserSettingsPanelOpen: boolean;
    isHelpMenuPanelOpen: boolean;
    isSignedIn: boolean;
    onDevMenuButtonClicked: () => void;
    onUserSettingsButtonClicked: () => void;
    onHelpMenuButtonClicked: () => void;
    statusForGetOrganization: Status;
    statusForGetSignedInUser: Status;
    statusForListTenants: Status;
    devBoxCreateAbilityState: DevBoxCreateAbilityState;
    environmentCreateAbilityState: EnvironmentCreateAbilityState;
    onDismiss: () => void;
    isHomeInitialized: boolean;
    isSettingsMenuTeachBubbleDismissed: boolean;
    isConnectViaAppTeachingBubbleDismissed: boolean;
    hasUserConnectedTheFirstTime: boolean;
    onSignInSubmitted: ReturnVoid<typeof signIn>;
    isScrolled?: boolean;
}

const messages = defineMessages({
    devMenuButtonAriaLabel: {
        id: 'TitleBar_DevMenuButton_AriaLabel',
        defaultMessage: 'Developer Menu',
        description: 'Aria label for dev menu button',
    },
    helpMenuButtonLabel: {
        id: 'TitleBar_HelpMenuButton_Label',
        defaultMessage: 'Help menu',
        description: 'Aria label for help menu (a.k.a "help" control) button',
    },
    meControlButtonLabel: {
        id: 'TitleBar_MeControlButton_Label',
        defaultMessage: 'User menu',
        description: 'Aria label for user menu (a.k.a "me" control) button',
    },
    buttonDefaultText: {
        id: 'TenantPicker_Button_DefaultText',
        defaultMessage: 'Select an organization',
        description: 'Button text used when no tenant (organization) is currently selected.',
    },
    userSettingsButtonAriaLabel: {
        id: 'TitleBar_UserSettingsButton_AriaLabel',
        defaultMessage: 'User settings menu',
        description: 'Aria label for user settings menu button',
    },
    userSettingsTeachBubbleTitle: {
        id: 'TitleBar_UserSettingsTeachBubble_Title',
        defaultMessage: 'Connection preferences',
        description: 'Title for the user settings teach bubble',
    },
});

/**
 * Styles
 */

const titleBarStylesFactory = (showBorder: boolean) =>
    makeStyles((theme) => ({
        root: {
            backgroundColor: getSemanticColor(theme, AppSemanticColor.transparentBackground),
            borderBottom: showBorder ? `1px solid ${theme.palette.neutralQuaternary}` : 0,
            height: '47px',
            // Disables text highlighting
            userSelect: 'none',
            width: '100%',
            zIndex: 1000,
        },
        alt: {
            backgroundColor: getSemanticColor(theme, AppSemanticColor.transparentBackground),
            borderBottom: showBorder ? `1px solid ${theme.palette.neutralQuaternary}` : 0,
            height: '47px',
            // Disables text highlighting
            userSelect: 'none',
            width: '100%',
            zIndex: 1000,
            justifyContent: 'center',
            display: 'flex',
        },
    }));

const useCommandBarStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: getSemanticColor(theme, AppSemanticColor.transparentBackground),
        height: '100%',
        padding: 0,
    },
}));

const useCommonCommandBarButtonStyles = makeStyles({
    root: {
        padding: '0',
        width: '48px',
    },
});

const useTitleBarContentStyles = makeStyles({
    root: {
        alignItems: 'center',
        // Include the left padding within the 100% width so the row doesn't overflow to the right
        // (which would push the far items past the header edge and clip the last one).
        boxSizing: 'border-box',
        display: 'flex',
        height: '100%',
        width: '100%',
        // Title/logo sits on the left; the CommandBar (far items only) is pushed to the right edge.
        justifyContent: 'space-between',
        // Left indent previously provided by the CommandBar padding, moved here now that the
        // title renders alongside (outside) the CommandBar.
        paddingLeft: '16px',
    },
});

/**
 * End Styles
 */

// The title/logo renders outside the CommandBar. A CommandBar is a role="menubar", whose only
// allowed children are menu items; rendering the branding (img + h1) as a command bar item made
// them disallowed, focusable menubar children (WCAG 1.3.1 / aria-required-children).
const emptyCommandBarItems: ICommandBarItemProps[] = [];

export const TitleBarComponent: React.FC<TitleBarComponentProps> = React.memo((props: TitleBarComponentProps) => {
    const {
        hasMultipleTenants,
        isDevMenuPanelOpen,
        isHelpMenuPanelOpen,
        isUserSettingsPanelOpen: isSettingsPanelOpen,
        isSignedIn,
        onDevMenuButtonClicked,
        onUserSettingsButtonClicked: onSettingsButtonClicked,
        onHelpMenuButtonClicked,
        statusForGetOrganization,
        statusForGetSignedInUser,
        statusForListTenants,
        devBoxCreateAbilityState,
        environmentCreateAbilityState,
        onDismiss,
        isSettingsMenuTeachBubbleDismissed,
        isConnectViaAppTeachingBubbleDismissed,
        hasUserConnectedTheFirstTime,
        isHomeInitialized,
        onSignInSubmitted,
        isScrolled,
    } = props;

    // Fluent hooks Creates unique ID for settings menu globally
    const settingsMenuButtonId = useId('settings-menu');
    const devMenuButtonRestoreFocusAttributes = useRestoreFocusTarget();
    const helpMenuButtonRestoreFocusAttributes = useRestoreFocusTarget();
    const settingsMenuButtonRestoreFocusAttributes = useRestoreFocusTarget();

    // State to hold the popover anchor element
    const [settingsMenuTargetElement, setSettingsMenuTargetElement] = React.useState<Element | null>(null);

    // This is done to ensure the element is loaded into DOM before we anchor the popover onto it.
    React.useEffect(() => {
        const settingsMenuTargetElement = document.querySelector(`[aria-label="User settings menu"]`);
        setSettingsMenuTargetElement(settingsMenuTargetElement);
    }, []);

    // Intl hooks
    const { formatMessage } = useIntl();

    // Theming hooks
    const theme = useCurrentFluent2Theme();

    // Memoized data
    // HACK: to correctly display progress bar, this non-subapp component needs awareness of subapp loading. May want to
    // think about how to detangle this in the future, but low priority for now since there's only one subapp.
    const showBorder =
        !isSignedIn ||
        (devBoxCreateAbilityState.createAbility !== ActionAbility.Unknown &&
            environmentCreateAbilityState.createAbility !== ActionAbility.Unknown);

    // Style hooks
    const commandBarButtonStyles = useCommonCommandBarButtonStyles();
    const commandBarStyles = useCommandBarStyles();
    const useTitleBarStyles = titleBarStylesFactory(showBorder);
    const titleBarStyles = useTitleBarStyles();
    const titleBarContentStyles = useTitleBarContentStyles();

    // Memoized data
    const farItems = React.useMemo(
        () =>
            getFarItems(
                commandBarButtonStyles,
                formatMessage(messages.devMenuButtonAriaLabel),
                devMenuButtonRestoreFocusAttributes,
                hasMultipleTenants,
                formatMessage(messages.helpMenuButtonLabel),
                helpMenuButtonRestoreFocusAttributes,
                isDevMenuPanelOpen,
                isHelpMenuPanelOpen,
                isSettingsPanelOpen,
                isSignedIn,
                formatMessage(messages.meControlButtonLabel),
                onDevMenuButtonClicked,
                onHelpMenuButtonClicked,
                onSettingsButtonClicked,
                statusForGetSignedInUser,
                statusForGetOrganization,
                formatMessage(messages.buttonDefaultText),
                statusForListTenants,
                formatMessage(messages.userSettingsButtonAriaLabel),
                settingsMenuButtonRestoreFocusAttributes,
                settingsMenuButtonId
            ),
        [
            commandBarButtonStyles,
            devMenuButtonRestoreFocusAttributes,
            formatMessage,
            hasMultipleTenants,
            helpMenuButtonRestoreFocusAttributes,
            isDevMenuPanelOpen,
            isSettingsPanelOpen,
            isHelpMenuPanelOpen,
            isSignedIn,
            onDevMenuButtonClicked,
            onHelpMenuButtonClicked,
            onSettingsButtonClicked,
            statusForGetOrganization,
            statusForGetSignedInUser,
            statusForListTenants,
            settingsMenuButtonRestoreFocusAttributes,
        ]
    );

    if (isScrolled) {
        return (
            <header className={titleBarStyles.alt} role="banner">
                <SignInButton onSignInSubmitted={onSignInSubmitted} />
            </header>
        );
    }

    /** Settings menu teach bubble should not be already dismissed + user must connect first time + home must be initialized + 
    connect via app teach bubble should be dismissed + welcome tour should be seen or skipped **/
    const shouldShowTeachingBubble =
        !isSettingsMenuTeachBubbleDismissed &&
        isHomeInitialized &&
        isConnectViaAppTeachingBubbleDismissed &&
        !!settingsMenuTargetElement &&
        hasUserConnectedTheFirstTime;

    return (
        <FluentProvider theme={theme}>
            <header className={titleBarStyles.root} role="banner">
                <div className={titleBarContentStyles.root}>
                    <Title />
                    {/* Skip the CommandBar when it has no items: Fluent's empty role="menubar"
                        trips aria-required-children (e.g. signed-out Public state). */}
                    {farItems.length > 0 && (
                        <CommandBar farItems={farItems} items={emptyCommandBarItems} styles={commandBarStyles} />
                    )}
                </div>
                {shouldShowTeachingBubble && (
                    <TeachingPopover
                        onOpenChange={onDismiss}
                        defaultOpen
                        appearance="brand"
                        positioning={{ target: settingsMenuTargetElement, position: 'after' }}
                    >
                        <TeachingPopoverSurface>
                            <TeachingPopoverHeader>
                                <FormattedMessage
                                    id="TitleBar_SettingsMenuPopover_Header"
                                    defaultMessage="Tip"
                                    description="Header for the settings menu popover"
                                />
                            </TeachingPopoverHeader>
                            <TeachingPopoverBody>
                                <TeachingPopoverTitle>
                                    <FormattedMessage
                                        id="TitleBar_SettingsMenuPopover_Title"
                                        defaultMessage="Connection settings"
                                        description="Title for the settings menu popover"
                                    />
                                </TeachingPopoverTitle>
                                <FormattedMessage
                                    id="TitleBar_SettingsMenuPopover_Body"
                                    defaultMessage="You can change the dev box connection settings at any time."
                                    description="Body for the settings menu popover"
                                />
                            </TeachingPopoverBody>
                        </TeachingPopoverSurface>
                    </TeachingPopover>
                )}
            </header>
        </FluentProvider>
    );
});

type TitleBarContainerProps = {
    isScrolled?: boolean;
};

export const TitleBarContainer: React.FC<TitleBarContainerProps> = (props) => {
    const { isScrolled } = props;

    // Application state hooks
    const hasMultipleTenants = useSelector(getHasMultipleTenants);
    const isSignedIn = useSelector(getIsSignedIn);
    const statusForGetOrganization = useSelector(getStatusForGetOrganization);
    const statusForGetSignedInUser = useSelector(getStatusForGetSignedInUser);
    const statusForListTenants = useSelector(getStatusForListTenants);
    const devBoxCreateAbilityState = useSelector(getDevBoxCreateAbilityState);
    const environmentCreateAbilityState = useSelector(getEnvironmentCreateAbilityState);
    const isSettingsMenuTeachBubbleDismissed = useSelector(getIsSettingsMenuTeachBubbleDismissed);
    const isConnectViaAppTeachBubbleDismissed = useSelector(getIsConnectViaAppTeachBubbleDismissed);
    const hasUserConnectedTheFirstTime = !!useSelector(getRemoteAppOptionsSelector);
    const isHomeInitialized = useSelector(getIsHomeInitialized);

    const dismissContent = useActionCreator(dismissContentActionCreator);
    const onSignInSubmitted = useActionCreator(signIn);

    const onDismiss = React.useCallback(() => {
        dismissContent({ content: DismissableContentName.SettingsMenuTeachableBubble });
    }, [dismissContent]);

    // Context hooks
    const { isOpen: isDevMenuPanelOpen, openSurface: onDevMenuButtonClicked } = useDevMenuPanelContext();
    const { isOpen: isHelpMenuPanelOpen, openSurface: onHelpMenuButtonClicked } = useHelpMenuPanelContext();
    const { isOpen: isUserSettingsPanelOpen, openSurface: onUserSettingsButtonClicked } = useUserSettingsPanelContext();

    return (
        <TitleBarComponent
            hasMultipleTenants={hasMultipleTenants}
            isDevMenuPanelOpen={isDevMenuPanelOpen}
            isUserSettingsPanelOpen={isUserSettingsPanelOpen}
            isHelpMenuPanelOpen={isHelpMenuPanelOpen}
            isSignedIn={isSignedIn}
            onDevMenuButtonClicked={onDevMenuButtonClicked}
            onUserSettingsButtonClicked={onUserSettingsButtonClicked}
            onHelpMenuButtonClicked={onHelpMenuButtonClicked}
            statusForGetOrganization={statusForGetOrganization}
            statusForGetSignedInUser={statusForGetSignedInUser}
            statusForListTenants={statusForListTenants}
            devBoxCreateAbilityState={devBoxCreateAbilityState}
            environmentCreateAbilityState={environmentCreateAbilityState}
            onDismiss={onDismiss}
            isHomeInitialized={isHomeInitialized}
            isSettingsMenuTeachBubbleDismissed={isSettingsMenuTeachBubbleDismissed}
            isConnectViaAppTeachingBubbleDismissed={isConnectViaAppTeachBubbleDismissed}
            hasUserConnectedTheFirstTime={hasUserConnectedTheFirstTime}
            onSignInSubmitted={onSignInSubmitted}
            isScrolled={isScrolled}
        />
    );
};

export default TitleBarContainer;
